<template>
    <rkm-modal
        :loading="loading"
        persistent
        ref="Modal"
        title="Assinatura"
    >
        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-row>
                <rkm-col>
                    <rkm-form-select
                        :options="certificates"
                        :validations="validations.certificate"
                        label="Certificado"
                        show-default-option
                        textField="subjectName"
                        v-model="thumbprint"
                        valueField="thumbprint"
                    >
                        <rkm-input-group-append slot="append">
                            <rkm-button
                                @click.stop="reloadCertificates"
                                color="primary"
                                title="Recarregar certificados"
                                v-rkm-tooltip.hover
                            >
                                <rkm-icon icon="sync-alt" />
                            </rkm-button>
                        </rkm-input-group-append>
                    </rkm-form-select>
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <template #footer="{ cancel }">
            <rkm-button
                v-if="allowCancel"
                :disabled="!!loading"
                @click="cancel"
            >
                Cancelar
            </rkm-button>

            <rkm-button
                v-if="!noAllowSkipSign"
                :disabled="!!loading"
                @click="skipSign"
            >
                {{ skipSignText }}
            </rkm-button>

            <rkm-button
                :disabled="!!loading"
                @click="$refs.Form.validate(sign)"
                color="primary"
            >
                Assinar
            </rkm-button>
        </template>
    </rkm-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { webPki, file, error, repository } from '@/mixins'

export default {
    computed: {
        ...mapGetters({ loading: 'getLoading', lotacao: 'getLotacao' }),
        validations() {

            return {
                certificate: { required: true },
            }
        },
    },
    created() {
        this.repositories = {
            restPki: this.getConectaSusRepository('restPki'),
        }
    },
    data: () => ({
        certificates: [],
        thumbprint: null,
        isSingleFile: false,
        files: [],
        fileNames: [],
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        open(files, fileNames) {

            if (!Array.isArray(files)) {

                this.isSingleFile = true

                files = [files]
            }

            if (!Array.isArray(fileNames)) {

                fileNames = [fileNames]
            }

            this.files = files
            this.fileNames = fileNames

            this.load()

            this.$refs.Modal.show()
        },
        load() {

            this.setLoading()

            this.init()
                .then(() => this.loadCertificates())
                .catch(this.handlePkiError)
                .finally(() => this.unsetLoading())
        },
        signProvisorio(file) {
            return new Promise((resolve, reject) => {
                this.fileToBase64(file)
                    .then(base64 => {
                        this.repositories.restPki.start({base64})
                            .then(response => {
                                this.signWithRestPki(response.data.token, this.thumbprint)
                                    .then(token => {
                                        this.repositories.restPki.finish({token})
                                            .then(response => {
                                                resolve(response)
                                            })
                                            .catch(reject)
                                    })
                                    .catch(reject)
                            })
                            .catch(reject)
                    })
                    .catch(reject)
            })
        },
        reloadCertificates() {

            this.thumbprint = null

            this.loadCertificates()
        },
        loadCertificates() {

            this.setLoading()

            this.listCertificates({
                filter: cert => cert.pkiBrazil.cpf === this.lotacao.usuario.cidadao.cpf
            })
                .then(certificates => this.certificates = certificates)
                .catch(this.handlePkiError)
                .finally(() => this.unsetLoading())
        },
        skipSign() {

            this.showConfirmation(this.skipSignConfirmationMessage,
                async () => {

                    this.$emit('skip-sign')
                    this.close()
                }
            )
        },
        sign() {

            this.setLoading()

            Promise.all(this.files.map(f => this.signProvisorio(f)))
                .then(async (signatures) => {

                    const signedFiles = signatures.map(s => s.data)

                    if (typeof this.afterSign === 'function') {

                        await this.afterSign(this.isSingleFile ? signedFiles[0] : signedFiles)
                    }

                    return signedFiles
                })
                .then(signedFiles => {

                    this.askForDownloadOrFinish(signedFiles).then(() => {

                        this.$emit('sign', this.isSingleFile ? signedFiles[0] : signedFiles)
                        this.close()
                    })
                })
                .catch(error => error && this.handleError(error))
                .finally(() => this.unsetLoading())
        },
        askForDownloadOrFinish(signedFiles) {

            return new Promise(resolve => {

                const download = () => {

                    signedFiles.forEach((f, i) => this.openInsteadOfDownload
                        ? this.getOpenMethod(this.files[i])(f)
                        : this.getDownloadMethod(this.files[i])(f, this.fileNames[i] || this.fileNames[0]))
                }

                if (this.downloadWithoutAsking) {

                    download()
                    resolve()
                }
                else if (this.noAskForDownload) {

                    resolve()
                }
                else {

                    const singleFileMessage = this.openInsteadOfDownload
                        ? 'Deseja abrir o documento assinado?'
                        : 'Deseja fazer o download do documento assinado?'

                    const multipleFileMessage = this.openInsteadOfDownload
                        ? 'Deseja abrir o(s) documento(s) assinado(s)?'
                        : 'Deseja fazer o(s) download(s) do(s) documento(s) assinado(s)?'

                    this.showConfirmation(this.isSingleFile ? singleFileMessage : multipleFileMessage,
                        async () => {

                            download()
                            resolve()
                        },
                        async () => resolve()
                    )
                }
            })
        },
        getSignMethod(file) {

            switch (this.getFileType(file)) {

                case 'pdf': return this.signPdf
                default: return null
            }
        },
        getDownloadMethod(file) {

            switch (this.getFileType(file)) {

                case 'pdf': return this.downloadPdfBase64
                default: return null
            }
        },
        getOpenMethod(file) {

            switch (this.getFileType(file)) {

                case 'pdf': return this.openPdfBase64
                default: return null
            }
        },
        getFileType(file) {

            return file.type
                .replaceAll(' ', '')
                .toLowerCase()
                .replaceAll('application/', '')
        },
        close() {

            this.reset()

            this.$refs.Modal.hide()
        },
        reset() {

            this.certificates = []
            this.thumbprint = null
            this.isSingleFile = false
            this.files = []
            this.fileNames = []

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
    },
    mixins: [webPki, file, error, repository],
    props: {
        afterSign: { type: Function },
        allowCancel: { type: Boolean, default: false },
        downloadWithoutAsking: { type: Boolean, default: false },
        noAllowSkipSign: { type: Boolean, default: false },
        noAskForDownload: { type: Boolean, default: false },
        openInsteadOfDownload: { type: Boolean, default: false },
        skipSignConfirmationMessage: { type: String, default: 'Deseja realmente não assinar?' },
        skipSignText: { type: String, default: 'Não assinar' },
    },
    watch: {
        certificates(certificates) {

            if (certificates.length === 1) {

                this.thumbprint = certificates[0].thumbprint
            }
        }
    }
}
</script>